// Account progression rules shared by the authority and the lobby. The server alone records outcomes.
import { CLASSES, MAX_LV, C_FISHER, C_DIVER, C_CHEF, C_ENGINEER, C_ROCK, C_RANGER, C_BLADE } from './data.js';

/** Historical qualifying-run counter only; hero achievements do not depend on time played. */
export const BONK_PLAY_SECONDS = 60;
// Unlock order is separate from stable class IDs on saves and the wire.
export const UNLOCK_ORDER = [C_FISHER, C_DIVER, C_CHEF, C_ENGINEER, C_ROCK, C_RANGER, C_BLADE];
export const unlockAfter = (cls) => { const i = UNLOCK_ORDER.indexOf(cls); return i >= 0 ? (UNLOCK_ORDER[i + 1] ?? -1) : -1; };
export const unlockBefore = (cls) => { const i = UNLOCK_ORDER.indexOf(cls); return i > 0 ? UNLOCK_ORDER[i - 1] : -1; };
// Stable keys and targets are shared with the authoritative simulation and UI. Amounts are capped at the goal.
export const HERO_GOALS = [
  { cls: C_DIVER, key: 'pearls', target: 50 },
  { cls: C_CHEF, key: 'chests', target: 1 },
  { cls: C_ENGINEER, key: 'weapon', target: 4, max: true },
  { cls: C_ROCK, key: 'lords', target: 1 },
  { cls: C_RANGER, key: 'damage', target: 3000 },
  { cls: C_BLADE, key: 'kills', target: 100 },
];
export const MILESTONE_LIMITS = Object.fromEntries(HERO_GOALS.map((g) => [g.key, g.target]));
export const freshMilestones = () => ({ v: 1, pearls: 0, chests: 0, weapon: 0, lords: 0, damage: 0, kills: 0 });
export function normalizeMilestones(raw) {
  const out = freshMilestones();
  if (raw?.v === 1) for (const { key, target } of HERO_GOALS) {
    const n = raw[key]; out[key] = Number.isFinite(n) ? Math.min(target, Math.max(0, n)) : 0;
  }
  return out;
}
export const allClasses = () => (1 << CLASSES.length) - 1;
/** @returns {{v:number, startedAt:number, unlocked:number, runs:number[], mastered:boolean, legacy:boolean, legacyServers:string[]}} */
export const freshProgress = () => ({ v: 1, startedAt: Date.now(), unlocked: 1 << C_FISHER, runs: Array(CLASSES.length).fill(0), mastered: false, legacy: false, legacyServers: [] });
export function normalizeProgress(raw, legacy = false) {
  const p = freshProgress();
  if (raw?.v === 1) {
    if (Number.isFinite(raw.startedAt) && raw.startedAt > 0) p.startedAt = raw.startedAt;
    p.unlocked = (Number.isInteger(raw.unlocked) ? raw.unlocked : 1) & allClasses();
    p.unlocked |= 1 << C_FISHER;
    p.runs = p.runs.map((_, i) => Number.isSafeInteger(raw.runs?.[i]) ? Math.max(0, raw.runs[i]) : 0);
    p.mastered = raw.mastered === true; p.legacy = raw.legacy === true;
    if (Array.isArray(raw.legacyServers)) p.legacyServers = [...new Set(raw.legacyServers.filter((s) => typeof s === 'string' && /^[0-9a-f-]{36}$/.test(s)))].slice(-32);
  }
  if (legacy || p.legacy) { p.unlocked = allClasses(); p.legacy = true; }
  return p;
}
export function canPlay(p, cls) { return Number.isInteger(cls) && cls >= 0 && cls < CLASSES.length && !!(normalizeProgress(p).unlocked & (1 << cls)); }
/** Applies only a result verified by the Run, and only for a class already unlocked at its start. */
export function applyBonkResult(raw, result) {
  const p = normalizeProgress(raw), cls = result?.cls;
  if (!canPlay(p, cls) || !Number.isFinite(result.activeSeconds) || result.activeSeconds < BONK_PLAY_SECONDS) return p;
  p.runs[cls] = Math.min(Number.MAX_SAFE_INTEGER, p.runs[cls] + 1);
  if (result.mastered === true || [result.weapons, result.passives].some((levels) => Array.isArray(levels) && levels.some((n) => Number.isInteger(n) && n >= MAX_LV))) p.mastered = true;
  return p;
}
/** A receipt remembers what this specific run unlocked, even if other runs later change the account. */
export function recordBonkResult(raw, result) {
  return { progress: applyBonkResult(raw, result), unlocked: -1 };
}
/** Run snapshots are cumulative. A per-run receipt keeps their component-wise high water marks, so retries,
 * out-of-order replies and resuming the same run add only new verified events. */
export function recordBonkMilestones(raw, rawGoals, rawReceipt, result) {
  if (result?.milestones?.v !== 1) throw new Error('invalid milestones');
  let progress = normalizeProgress(raw);
  const goals = normalizeMilestones(rawGoals), prior = normalizeMilestones(rawReceipt?.milestones), current = normalizeMilestones(result.milestones);
  const newly = [];
  for (const { cls, key, target, max } of HERO_GOALS) {
    const amount = Math.max(prior[key], current[key]);
    goals[key] = Math.min(target, max ? Math.max(goals[key], amount) : goals[key] + amount - prior[key]);
    prior[key] = amount;
    if (goals[key] >= target && !canPlay(progress, cls)) { progress.unlocked |= 1 << cls; newly.push(cls); }
  }
  const unlocked = [...new Set([...(Array.isArray(rawReceipt?.unlocked) ? rawReceipt.unlocked.filter((n) => Number.isInteger(n) && n >= 0 && n < CLASSES.length) : []), ...newly])];
  const final = rawReceipt?.final === true || result.final === true;
  if (result.final === true && rawReceipt?.final !== true) progress = applyBonkResult(progress, result);
  return { progress, goals, receipt: { milestones: prior, unlocked, final }, unlocked };
}
